import {
  adaptLegacyViewConfig,
  buildLegacyShapeFromGridState,
  sanitizeGridState,
} from '../data-models/adapters/legacyViewConfigAdapter';
import { IViewDataModel, CreateViewPayload, IViewListItemDataModel } from '../data-models/view.data-model';
import {
  COMPANY_VIEW_TYPE,
  VIEW_TYPE,
  ViewConfigDataModel,
  ViewOrder,
  ViewSections,
  ViewTypeByData,
} from '../data-models/view-config.data-model';
import { IFundDataModel } from '../data-models/fund.data-model';

const VERSION = 1;

export interface ViewModel extends ViewConfigDataModel {
  companyViewType: COMPANY_VIEW_TYPE;
  createdBy: string;
  filteredFunds: IFundDataModel[] | null;
  id: number;
  isDefault: boolean;
  viewType: ViewTypeByData;
  label: string;
  name: string;
  order: ViewOrder;
  origin: null | ViewModel; // null for VIEW_TYPE.COMPANY, View for personal/custom views, used for resetting
  sections: ViewSections;
  sharing?: string[];
  type: VIEW_TYPE;
  viewId: string;
}

/**
 * A view as the list knows it: who it belongs to, its name and type, and who it is shared with. Its settings
 * (columns, sections, funds) arrive with GET /view/:id when it opens, as a {@link ViewModel}.
 */
export type ViewSummaryModel = Pick<
  ViewModel,
  'createdBy' | 'id' | 'isDefault' | 'viewType' | 'label' | 'name' | 'sharing' | 'type' | 'viewId'
>;

export function deserializeViewSummary(viewDataModel: IViewListItemDataModel): ViewSummaryModel {
  return {
    createdBy: viewDataModel.createdBy ?? '',
    id: viewDataModel.id || 0,
    isDefault: viewDataModel.isDefault,
    viewType: viewDataModel.viewType,
    label: viewDataModel.displayName,
    name: viewDataModel.name,
    sharing: viewDataModel.sharing?.map((item) => item.userId) ?? [],
    type: getViewType(viewDataModel.isDefault, viewDataModel.shared ?? false),
    viewId: viewDataModel.viewId,
  };
}

export function toViewSummary(view: ViewSummaryModel): ViewSummaryModel {
  const { createdBy, id, isDefault, viewType, label, name, sharing, type, viewId } = view;
  return { createdBy, id, isDefault, viewType, label, name, sharing, type, viewId };
}

export function deserializeView(viewDataModel: IViewDataModel): ViewModel {
  const adaptedConfig = adaptLegacyViewConfig(viewDataModel.config ?? {}, {
    onLegacyHit: () => {
      // TODO(FD-2977 follow-up): wire this to the project's telemetry helper.
      // For now, emit a single console.info so legacy-hit traffic is observable
      // in dev / via prod log scraping until the helper lands.
      console.info('legacyViewConfigAdapter:hit');
    },
  });

  // Defensive sanitisation: strip null/undefined entries and entries with no
  // colId from every gridState array slice. Guards against historical data
  // corruption (e.g. null in columnSizingModel) that would otherwise crash
  // ag-grid's StateService when applying initialState.
  const sanitizedGridState = sanitizeGridState(adaptedConfig.gridState, {
    onCorruptionDetected: (slice) => {
      console.warn(`gridStateSanitizer:corruption-detected slice=${slice}`);
    },
  });

  return {
    ...adaptedConfig,
    gridState: sanitizedGridState,
    createdBy: viewDataModel.createdBy ?? '',
    id: viewDataModel.id || 0,
    isDefault: viewDataModel.isDefault,
    viewType: viewDataModel.viewType,
    label: viewDataModel.displayName,
    name: viewDataModel.name,
    sharing: viewDataModel.sharing?.map((item) => item.userId) ?? [],
    type: getViewType(viewDataModel.isDefault, viewDataModel.shared ?? false),
    viewId: viewDataModel.viewId,
  } as ViewModel;
}

export function serializeView(viewModel: ViewModel): IViewDataModel {
  const { createdBy, id, label, name, sharing, type, viewId, viewType, ...config } = viewModel;

  // Dual-write rollback safety (FD-2977): when the view carries a gridState,
  // also project an equivalent legacy-shape (`components.table`, `rowGroupModel`,
  // `tableFilters`) alongside it. A rolled-back client reads the legacy keys
  // and renders correctly. The new client reads gridState. Remove once
  // legacyViewConfigAdapter:hit telemetry decays to zero.
  // `origin` is intentionally not dual-written — per lead, it's a legacy reset
  // baseline and not worth the round-trip risk.
  const legacyProjection = buildLegacyShapeFromGridState(config.gridState);
  const dualWriteConfig = { ...config, ...legacyProjection };

  return {
    createdBy,
    config: dualWriteConfig,
    displayName: label,
    id,
    isDefault: type === VIEW_TYPE.COMPANY,
    viewType,
    name,
    sharing: sharing?.map((item) => {
      return { userId: item };
    }),
    shared: viewId === VIEW_TYPE.SHARED,
    version: VERSION,
    viewId,
  };
}

export function serializeViewForCreation(viewModel: ViewModel): CreateViewPayload {
  const newView = serializeView(viewModel);
  delete newView.id;

  return newView;
}

function getViewType(isDefault: boolean, isShared: boolean): VIEW_TYPE {
  if (isDefault) {
    return VIEW_TYPE.COMPANY;
  }
  if (isShared) {
    return VIEW_TYPE.SHARED;
  }
  return VIEW_TYPE.PERSONAL;
}

/**
 * The view identifier to send to analytics. Personal views have per-user unique
 * ids (`personal_<uuid>`) that aren't comparable across users and would bloat
 * facet cardinality, so collapse them to the low-cardinality `personal` bucket.
 * Company/default (e.g. `overview`, `return_forecast`) and shared views keep
 * their stable, org-wide `viewId` so per-view filtering stays meaningful.
 */
export function getTrackableViewId(view: Pick<ViewModel, 'type' | 'viewId'>): string {
  return view.type === VIEW_TYPE.PERSONAL ? VIEW_TYPE.PERSONAL : view.viewId;
}
